"use client";

import { Link, useLocation } from "react-router-dom";
import { LogIn, Menu, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../assets/logo.png";

const APP_URL = process.env.REACT_APP_DASHBOARD_URL;
export default function Header() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Careers", path: "/careers" },
    { name: "Help", path: "/help" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white backdrop-blur-xl shadow-lg shadow-black/5 `}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="relative z-10 flex items-center gap-3"
              onClick={() => setIsOpen(false)}
            >
              <img
                src={logo}
                alt="Unimerg Logo"
                className="h-8 lg:h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg group ${
                    isActive(link.path)
                      ? "text-[#8B52FF]"
                      : "text-gray-700 hover:text-[#8B52FF]"
                  }`}
                >
                  {link.name}
                  {/* Active indicator */}
                  {isActive(link.path) && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 mx-auto w-6 h-0.5 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}
                  {/* Hover effect */}
                  <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </Link>
              ))}
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to={`${APP_URL}/login`}>
                <button
                  className={`px-5 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 ${"border-[#8B52FF]/30 text-[#8B52FF] hover:bg-[#8B52FF]/5"}`}
                >
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Login
                  </span>
                </button>
              </Link>
              <Link to={`${APP_URL}/signup`}>
                <button className="px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] hover:shadow-lg hover:shadow-[#8B52FF]/25 hover:-translate-y-0.5 transition-all duration-200">
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Get Started
                  </span>
                </button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden relative z-[60] p-2 rounded-xl transition-colors duration-200 ${
                isOpen
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed top-20 left-4 right-4 z-50 lg:hidden"
            >
              <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 border border-gray-100 overflow-hidden">
                {/* Gradient accent line */}
                <div className="h-1 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]" />

                <div className="p-4">
                  {/* Navigation Links */}
                  <div className="space-y-1">
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={link.path}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                            isActive(link.path)
                              ? "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF]"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {link.name}
                          {isActive(link.path) && (
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]" />
                          )}
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="my-4 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <Link
                      to={`${APP_URL}/login`}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      <button className="w-full px-5 py-3 text-sm font-medium rounded-xl border border-[#8B52FF]/30 text-[#8B52FF] hover:bg-[#8B52FF]/5 transition-colors duration-200">
                        <span className="flex items-center justify-center gap-2">
                          <LogIn className="w-4 h-4" />
                          Login to Dashboard
                        </span>
                      </button>
                    </Link>
                    <Link
                      to={`${APP_URL}/signup`}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      <button className="w-full px-5 py-3 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] hover:shadow-lg hover:shadow-[#8B52FF]/25 transition-all duration-200">
                        <span className="flex items-center justify-center gap-2">
                          <UserPlus className="w-4 h-4" />
                          Create Free Account
                        </span>
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-16 lg:h-20" />
    </>
  );
}
